import { FileSpreadsheet, Tags } from 'lucide-react';
import type { NodeStruct } from '@/types/node-types';
import {
  JEV_CLASSIFIER_NODE_ID,
  describeJevOutcome,
  formatPercent,
  getJevConfig,
  rankedProbabilities,
  readJevResult,
  type JevClassification,
  type JevRunSummary,
} from '@/pages/workflow/helper/jev-classifier';
import { cn } from '@/lib/utils';

/** True when a stored node result is a Jev classification (single or batch). */
export const isJevResult = (result: unknown): boolean =>
  readJevResult(result) !== null;

function ProbabilityBar({
  label,
  probability,
  highlight,
  dimmed,
  testId,
}: {
  label: string;
  probability: number;
  highlight?: boolean;
  dimmed?: boolean;
  testId?: string;
}) {
  const percent = Math.max(0, Math.min(100, Math.round(probability * 100)));
  return (
    <div
      className={cn('flex items-center gap-2 text-xs', dimmed && 'opacity-60')}
      data-testid={testId}
    >
      <span
        className={cn(
          'w-[120px] shrink-0 truncate font-mono',
          highlight && 'font-semibold',
        )}
        title={label}
      >
        {label}
      </span>
      <div className='relative h-2 flex-1 rounded-full bg-muted overflow-hidden'>
        <div
          className={cn(
            'absolute inset-y-0 left-0 rounded-full',
            highlight ? 'bg-emerald-500' : 'bg-slate-400 dark:bg-slate-500',
          )}
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className='w-[40px] shrink-0 text-right tabular-nums'>
        {formatPercent(probability)}
      </span>
    </div>
  );
}

function SingleResult({
  classification,
}: {
  classification: JevClassification;
}) {
  const ranked = rankedProbabilities(classification);
  return (
    <div className='flex flex-col gap-2'>
      <div className='flex flex-wrap items-center gap-2'>
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-semibold font-mono text-white',
            classification.uncertain ? 'bg-amber-500' : 'bg-emerald-600',
          )}
          data-testid='jev-result-label'
        >
          <Tags className='size-3.5' />
          {classification.label ?? '—'}
        </span>
        <span
          className='text-xs text-muted-foreground'
          data-testid='jev-result-confidence'
        >
          {classification.uncertain
            ? `Jev's top pick was ${classification.topLabel ?? '—'} at ${formatPercent(classification.topProbability)}, below your threshold`
            : `confidence ${formatPercent(classification.topProbability)}`}
        </span>
      </div>
      {/* Confidence bar for the answer */}
      <div
        className='relative h-2.5 w-full rounded-full bg-muted overflow-hidden'
        data-testid='jev-confidence-bar'
        aria-label={`confidence ${formatPercent(classification.topProbability)}`}
      >
        <div
          className={cn(
            'absolute inset-y-0 left-0 rounded-full',
            classification.uncertain ? 'bg-amber-500' : 'bg-emerald-500',
          )}
          style={{
            width: `${Math.round((classification.topProbability ?? 0) * 100)}%`,
          }}
        />
      </div>
      {ranked.length > 0 && (
        <div className='flex flex-col gap-1' data-testid='jev-probabilities'>
          <span className='text-[11px] font-medium text-muted-foreground'>
            Every label&apos;s probability
          </span>
          {ranked.map(([label, probability]) => (
            <ProbabilityBar
              key={label}
              label={label}
              probability={probability}
              highlight={label === classification.topLabel}
              dimmed={label !== classification.topLabel}
              testId={`jev-probability-${label}`}
            />
          ))}
        </div>
      )}
      {Object.keys(classification.yesNo).length > 0 && (
        <div className='flex flex-col gap-1' data-testid='jev-yes-no'>
          <span className='text-[11px] font-medium text-muted-foreground'>
            Yes/no questions (probability of yes)
          </span>
          {Object.entries(classification.yesNo).map(([name, probability]) => (
            <ProbabilityBar
              key={name}
              label={name}
              probability={probability}
              highlight={probability >= 0.5}
              testId={`jev-yes-no-${name}`}
            />
          ))}
        </div>
      )}
      {classification.renormalised && (
        <p className='text-[11px] text-muted-foreground'>
          Jev&apos;s probabilities were rescaled to sum to 100%.
        </p>
      )}
    </div>
  );
}

function BatchResult({
  summary,
}: {
  summary: Extract<JevRunSummary, { kind: 'batch' }>;
}) {
  const total = summary.count || 1;
  return (
    <div className='flex flex-col gap-2' data-testid='jev-batch-result'>
      <div className='flex flex-wrap items-center gap-2 text-xs'>
        <span className='rounded-full bg-emerald-600 px-2.5 py-1 font-semibold text-white'>
          {summary.succeeded} of {summary.count} classified
        </span>
        {summary.failed > 0 && (
          <span className='rounded-full bg-red-500 px-2.5 py-1 font-semibold text-white'>
            {summary.failed} failed
          </span>
        )}
        {summary.hasSpreadsheet && (
          <span className='inline-flex items-center gap-1 text-muted-foreground'>
            <FileSpreadsheet className='size-3.5' /> spreadsheet attached (one
            row per item)
          </span>
        )}
      </div>
      {summary.labelCounts.length > 0 && (
        <div className='flex flex-col gap-1'>
          <span className='text-[11px] font-medium text-muted-foreground'>
            Items per label
          </span>
          {summary.labelCounts.map(([label, count], index) => (
            <ProbabilityBar
              key={label}
              label={`${label} (${count})`}
              probability={count / total}
              highlight={index === 0}
              testId={`jev-batch-label-${label}`}
            />
          ))}
        </div>
      )}
      {summary.failed > 0 && (
        <ul className='text-[11px] text-red-600 dark:text-red-300 list-disc pl-4'>
          {summary.rows
            .filter((row) => row.error)
            .slice(0, 5)
            .map((row) => (
              <li key={row.index}>
                Item {row.index + 1}: {row.error}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

/** The run read back: label, confidence, every probability, yes/no answers. */
export function JevResultCard({ summary }: { summary: JevRunSummary }) {
  return (
    <div
      className='rounded-lg border border-border bg-background p-3'
      data-testid='jev-result-card'
    >
      {summary.kind === 'single' ? (
        <SingleResult classification={summary.classification} />
      ) : (
        <BatchResult summary={summary} />
      )}
    </div>
  );
}

/**
 * The Jev run, above whatever else the result view shows (the JSON stays
 * below for the full record).
 */
export function JevResultView({ result }: { result: unknown }) {
  const summary = readJevResult(result);
  if (!summary) return null;
  return (
    <div className='mb-3' data-testid='jev-result-view'>
      <JevResultCard summary={summary} />
    </div>
  );
}

/**
 * One compact chip on the node body: the configuration before a run
 * ("3 labels · unsure below 60%"), the answer after it ("billing · 91%").
 */
export function JevNodeChip({
  struct,
  result,
}: {
  struct: NodeStruct;
  result: unknown;
}) {
  if (struct.node_id !== JEV_CLASSIFIER_NODE_ID) return null;
  const summary = readJevResult(result);
  if (summary) {
    const uncertain =
      summary.kind === 'single' && summary.classification.uncertain;
    return (
      <div className='px-4 pt-1' data-testid='jev-node-chip'>
        <span
          className={cn(
            'inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold text-white',
            uncertain ? 'bg-amber-500' : 'bg-emerald-600',
          )}
          title={describeJevOutcome(summary)}
        >
          <Tags className='size-3 shrink-0' />
          <span className='truncate'>{describeJevOutcome(summary)}</span>
        </span>
      </div>
    );
  }
  const config = getJevConfig(struct);
  const offered = config.labels.filter(
    (label) => label.name !== config.uncertainLabel,
  );
  const text =
    offered.length === 0
      ? config.questions.length
        ? `${config.questions.length} yes/no question${config.questions.length === 1 ? '' : 's'}`
        : 'no labels yet'
      : `${offered.length} labels${
          config.uncertainThreshold > 0
            ? ` · "${config.uncertainLabel}" below ${formatPercent(config.uncertainThreshold)}`
            : ''
        }`;
  return (
    <div className='px-4 pt-1' data-testid='jev-node-chip'>
      <span
        className='inline-flex max-w-full items-center gap-1 rounded-full border border-border bg-muted-background px-2 py-0.5 text-[11px] text-muted-foreground'
        title={offered.map((label) => label.name).join(', ')}
      >
        <Tags className='size-3 shrink-0' />
        <span className='truncate'>{text}</span>
      </span>
    </div>
  );
}
